/**
 * The Lillebjörn — shared sizing/fit business logic.
 *
 * Pure, framework-free engine copied out of lillebjorn-fit-finder.js
 * (the live drawer's data tables, breed resolution, sizing matrices,
 * guardrail/boundary rules, anatomical width matrix, leash rules, and
 * bundle pricing math) so a second surface (the Size Guide page) can use
 * the exact same rules without re-deriving them by hand.
 *
 * This is a COPY, not a refactor: lillebjorn-fit-finder.js is untouched
 * and keeps its own private copies of everything below. Nothing here has
 * any DOM/cart/Shopify-variant dependency — callers own their own
 * rendering and state.
 *
 * Exposed as the global `window.LillebjornFitLogic`.
 */
(function (global) {
  "use strict";

  /* ============================================================
     Breed database & resolution
     ============================================================ */

  var BREED_NAMES = [
    "Affenpinscher","Afghan Hound","Airedale Terrier","Akita","Alaskan Malamute",
    "American Bulldog","American Eskimo Dog","American Staffordshire Terrier",
    "Australian Cattle Dog","Australian Shepherd","Azawakh","Basenji","Basset Hound",
    "Beagle","Bearded Collie","Bedlington Terrier","Belgian Malinois",
    "Bernese Mountain Dog","Bichon Frise","Bloodhound","Border Collie","Border Terrier",
    "Borzoi","Boston Terrier","Bouvier des Flandres","Boxer","Boykin Spaniel",
    "Brittany","Bull Terrier","Bulldog","Bullmastiff","Cairn Terrier","Cane Corso",
    "Cardigan Welsh Corgi","Cavalier King Charles Spaniel","Chesapeake Bay Retriever",
    "Chihuahua","Chinese Crested","Chow Chow","Cocker Spaniel","Collie","Coonhound",
    "Dachshund (Miniature)","Dachshund (Standard)","Dalmatian","Doberman Pinscher",
    "Dogo Argentino","Dogue de Bordeaux","Dutch Shepherd","English Setter",
    "English Springer Spaniel","Field Spaniel","Flat-Coated Retriever","French Bulldog",
    "German Shepherd","German Shorthaired Pointer","Golden Retriever","Great Dane",
    "Great Pyrenees","Greyhound","Havanese","Ibizan Hound","Irish Setter",
    "Irish Wolfhound","Italian Greyhound","Jack Russell Terrier","Japanese Chin",
    "Keeshond","Kerry Blue Terrier","Komondor","Labrador Retriever","Lagotto Romagnolo",
    "Lhasa Apso","Maltese","Mastiff","Miniature Pinscher","Miniature Schnauzer",
    "Newfoundland","Norwegian Elkhound","Norwich Terrier","Old English Sheepdog",
    "Papillon","Pekingese","Pembroke Welsh Corgi","Pharaoh Hound","Pit Bull Terrier",
    "Pointer","Pomeranian","Poodle (Miniature)","Poodle (Standard)","Poodle (Toy)",
    "Portuguese Water Dog","Pug","Rat Terrier","Rhodesian Ridgeback","Rottweiler",
    "Saint Bernard","Saluki","Samoyed","Schnauzer","Scottish Terrier","Shar Pei",
    "Shetland Sheepdog","Shiba Inu","Shih Tzu","Siberian Husky","Sloughi",
    "Spanish Galgo","Springer Spaniel","Staffordshire Bull Terrier","Vizsla",
    "Weimaraner","Welsh Terrier","West Highland White Terrier","Whippet",
    "Xoloitzcuintli","Yorkshire Terrier"
  ];
  var FALLBACK_BREED_NAME = "Mixed Breed / Other";

  // Breeds with a hand-measured sizing profile (highest precision tier).
  // Every other name in BREED_NAMES resolves through the archetype/size-class
  // matrix below — nothing hits the Mixed Breed fallback except unmatched text.
  var CURATED_BREEDS = {
    "italian greyhound": { neck: [20, 27], chest: [36, 46], neckDefault: 23, chestDefault: 40, build: "Slender / Elegant", category: "sighthound" },
    "whippet": { neck: [27, 35], chest: [52, 63], neckDefault: 31, chestDefault: 58, build: "Slender / Elegant", category: "sighthound" },
    "greyhound": { neck: [32, 41], chest: [58, 72], neckDefault: 36, chestDefault: 64, build: "Slender / Elegant", category: "sighthound" },
    "saluki": { neck: [28, 37], chest: [54, 66], neckDefault: 32, chestDefault: 59, build: "Slender / Elegant", category: "sighthound" },
    "dachshund (standard)": { neck: [24, 31], chest: [36, 48], neckDefault: 27, chestDefault: 42, build: "Proportional / Low Profile", category: "standard" },
    "dachshund (miniature)": { neck: [17, 25], chest: [28, 39], neckDefault: 21, chestDefault: 34, build: "Petite / Low Profile", category: "standard" },
    "labrador retriever": { neck: [37, 48], chest: [66, 86], neckDefault: 42, chestDefault: 76, build: "Athletic / Standard", category: "standard" },
    "golden retriever": { neck: [38, 49], chest: [65, 84], neckDefault: 43, chestDefault: 75, build: "Athletic / Standard", category: "standard" },
    "german shepherd": { neck: [39, 51], chest: [62, 86], neckDefault: 46, chestDefault: 74, build: "Athletic / Standard", category: "standard" },
    "rottweiler": { neck: [44, 56], chest: [72, 94], neckDefault: 50, chestDefault: 82, build: "Heavy / Muscular", category: "bully" },
    "doberman pinscher": { neck: [38, 48], chest: [63, 80], neckDefault: 43, chestDefault: 71, build: "Athletic / Standard", category: "standard" },
    "boxer": { neck: [38, 49], chest: [64, 82], neckDefault: 43, chestDefault: 73, build: "Heavy / Muscular", category: "bully" },
    "siberian husky": { neck: [35, 45], chest: [58, 74], neckDefault: 39, chestDefault: 65, build: "Athletic / Standard", category: "standard" },
    "bernese mountain dog": { neck: [42, 54], chest: [76, 96], neckDefault: 48, chestDefault: 86, build: "Athletic / Standard", category: "standard" },
    "great dane": { neck: [46, 58], chest: [80, 100], neckDefault: 52, chestDefault: 90, build: "Athletic / Standard", category: "standard" },
    "border collie": { neck: [32, 42], chest: [52, 68], neckDefault: 36, chestDefault: 59, build: "Athletic / Standard", category: "standard" },
    "vizsla": { neck: [32, 41], chest: [55, 70], neckDefault: 36, chestDefault: 61, build: "Athletic / Standard", category: "standard" },
    "poodle (standard)": { neck: [31, 40], chest: [53, 68], neckDefault: 35, chestDefault: 59, build: "Athletic / Standard", category: "standard" },
    "poodle (miniature)": { neck: [22, 29], chest: [38, 49], neckDefault: 25, chestDefault: 43, build: "Petite / Low Profile", category: "toy" },
    "cocker spaniel": { neck: [27, 35], chest: [46, 60], neckDefault: 30, chestDefault: 52, build: "Proportional / Low Profile", category: "standard" },
    "beagle": { neck: [28, 36], chest: [46, 59], neckDefault: 31, chestDefault: 51, build: "Compact / Broad", category: "standard" },
    "french bulldog": { neck: [28, 36], chest: [47, 61], neckDefault: 32, chestDefault: 54, build: "Compact / Broad", category: "bully" },
    "boston terrier": { neck: [25, 33], chest: [41, 53], neckDefault: 28, chestDefault: 46, build: "Compact / Broad", category: "bully" },
    "pug": { neck: [24, 32], chest: [40, 52], neckDefault: 27, chestDefault: 45, build: "Compact / Broad", category: "bully" },
    "shih tzu": { neck: [20, 27], chest: [34, 44], neckDefault: 23, chestDefault: 38, build: "Petite / Low Profile", category: "toy" },
    "cavalier king charles spaniel": { neck: [21, 29], chest: [36, 47], neckDefault: 24, chestDefault: 41, build: "Petite / Low Profile", category: "toy" },
    "pembroke welsh corgi": { neck: [28, 36], chest: [48, 62], neckDefault: 31, chestDefault: 54, build: "Compact / Broad", category: "standard" },
    "chihuahua": { neck: [16, 22], chest: [26, 35], neckDefault: 18, chestDefault: 30, build: "Petite / Low Profile", category: "toy" }
  };

  // Universal archetype / size-class engine: every breed NOT in
  // CURATED_BREEDS above still derives real, personalized sizing data from
  // a size-class baseline (absolute circumference band) adjusted by a
  // body-shape archetype multiplier, instead of degrading to the generic
  // Mixed Breed fallback.
  var SIZE_CLASS_BASE = {
    toy: { neck: [16, 22], chest: [26, 36] },
    small: { neck: [22, 30], chest: [36, 50] },
    medium: { neck: [30, 40], chest: [50, 66] },
    large: { neck: [38, 50], chest: [64, 84] },
    giant: { neck: [46, 60], chest: [80, 104] }
  };

  // The 4 strict sizing brackets. sighthound narrows the neck relative to a
  // deep chest; bully (brachycephalic/broad) thickens the neck base for
  // powerful shoulders; toy/standard pass the size-class baseline through.
  var ARCHETYPE_MULTIPLIER = {
    sighthound: { neck: 0.90, chest: 1.08 },
    bully: { neck: 1.14, chest: 1.06 },
    toy: { neck: 1.00, chest: 1.00 },
    standard: { neck: 1.00, chest: 1.00 }
  };

  var BREED_ARCHETYPES = {
    "affenpinscher": { archetype: "toy", sizeClass: "toy" },
    "afghan hound": { archetype: "sighthound", sizeClass: "large" },
    "airedale terrier": { archetype: "standard", sizeClass: "medium" },
    "akita": { archetype: "standard", sizeClass: "large" },
    "alaskan malamute": { archetype: "standard", sizeClass: "large" },
    "american bulldog": { archetype: "bully", sizeClass: "large" },
    "american eskimo dog": { archetype: "standard", sizeClass: "small" },
    "american staffordshire terrier": { archetype: "bully", sizeClass: "medium" },
    "australian cattle dog": { archetype: "standard", sizeClass: "medium" },
    "australian shepherd": { archetype: "standard", sizeClass: "medium" },
    "azawakh": { archetype: "sighthound", sizeClass: "medium" },
    "basenji": { archetype: "standard", sizeClass: "small" },
    "basset hound": { archetype: "standard", sizeClass: "medium" },
    "bearded collie": { archetype: "standard", sizeClass: "medium" },
    "bedlington terrier": { archetype: "standard", sizeClass: "small" },
    "belgian malinois": { archetype: "standard", sizeClass: "medium" },
    "bichon frise": { archetype: "toy", sizeClass: "toy" },
    "bloodhound": { archetype: "standard", sizeClass: "large" },
    "border terrier": { archetype: "standard", sizeClass: "small" },
    "borzoi": { archetype: "sighthound", sizeClass: "large" },
    "bouvier des flandres": { archetype: "standard", sizeClass: "large" },
    "boykin spaniel": { archetype: "standard", sizeClass: "small" },
    "brittany": { archetype: "standard", sizeClass: "small" },
    "bull terrier": { archetype: "bully", sizeClass: "medium" },
    "bulldog": { archetype: "bully", sizeClass: "medium" },
    "bullmastiff": { archetype: "bully", sizeClass: "giant" },
    "cairn terrier": { archetype: "standard", sizeClass: "small" },
    "cane corso": { archetype: "bully", sizeClass: "giant" },
    "cardigan welsh corgi": { archetype: "standard", sizeClass: "small" },
    "chesapeake bay retriever": { archetype: "standard", sizeClass: "large" },
    "chinese crested": { archetype: "toy", sizeClass: "toy" },
    "chow chow": { archetype: "bully", sizeClass: "medium" },
    "collie": { archetype: "standard", sizeClass: "large" },
    "coonhound": { archetype: "standard", sizeClass: "large" },
    "dalmatian": { archetype: "standard", sizeClass: "large" },
    "dogo argentino": { archetype: "bully", sizeClass: "large" },
    "dogue de bordeaux": { archetype: "bully", sizeClass: "large" },
    "dutch shepherd": { archetype: "standard", sizeClass: "medium" },
    "english setter": { archetype: "standard", sizeClass: "large" },
    "english springer spaniel": { archetype: "standard", sizeClass: "medium" },
    "field spaniel": { archetype: "standard", sizeClass: "small" },
    "flat-coated retriever": { archetype: "standard", sizeClass: "large" },
    "german shorthaired pointer": { archetype: "standard", sizeClass: "large" },
    "great pyrenees": { archetype: "standard", sizeClass: "giant" },
    "havanese": { archetype: "toy", sizeClass: "toy" },
    "ibizan hound": { archetype: "sighthound", sizeClass: "large" },
    "irish setter": { archetype: "standard", sizeClass: "large" },
    "irish wolfhound": { archetype: "sighthound", sizeClass: "giant" },
    "jack russell terrier": { archetype: "standard", sizeClass: "small" },
    "japanese chin": { archetype: "toy", sizeClass: "toy" },
    "keeshond": { archetype: "standard", sizeClass: "medium" },
    "kerry blue terrier": { archetype: "standard", sizeClass: "medium" },
    "komondor": { archetype: "standard", sizeClass: "giant" },
    "lagotto romagnolo": { archetype: "standard", sizeClass: "small" },
    "lhasa apso": { archetype: "toy", sizeClass: "toy" },
    "maltese": { archetype: "toy", sizeClass: "toy" },
    "mastiff": { archetype: "bully", sizeClass: "giant" },
    "miniature pinscher": { archetype: "toy", sizeClass: "toy" },
    "miniature schnauzer": { archetype: "toy", sizeClass: "small" },
    "newfoundland": { archetype: "standard", sizeClass: "giant" },
    "norwegian elkhound": { archetype: "standard", sizeClass: "medium" },
    "norwich terrier": { archetype: "standard", sizeClass: "small" },
    "old english sheepdog": { archetype: "standard", sizeClass: "large" },
    "papillon": { archetype: "toy", sizeClass: "toy" },
    "pekingese": { archetype: "toy", sizeClass: "toy" },
    "pharaoh hound": { archetype: "sighthound", sizeClass: "medium" },
    "pit bull terrier": { archetype: "bully", sizeClass: "medium" },
    "pointer": { archetype: "standard", sizeClass: "large" },
    "pomeranian": { archetype: "toy", sizeClass: "toy" },
    "poodle (toy)": { archetype: "toy", sizeClass: "toy" },
    "portuguese water dog": { archetype: "standard", sizeClass: "medium" },
    "rat terrier": { archetype: "standard", sizeClass: "small" },
    "rhodesian ridgeback": { archetype: "standard", sizeClass: "large" },
    "saint bernard": { archetype: "bully", sizeClass: "giant" },
    "samoyed": { archetype: "standard", sizeClass: "medium" },
    "schnauzer": { archetype: "standard", sizeClass: "medium" },
    "scottish terrier": { archetype: "standard", sizeClass: "small" },
    "shar pei": { archetype: "bully", sizeClass: "medium" },
    "shetland sheepdog": { archetype: "standard", sizeClass: "small" },
    "shiba inu": { archetype: "standard", sizeClass: "small" },
    "sloughi": { archetype: "sighthound", sizeClass: "medium" },
    "spanish galgo": { archetype: "sighthound", sizeClass: "medium" },
    "springer spaniel": { archetype: "standard", sizeClass: "medium" },
    "staffordshire bull terrier": { archetype: "bully", sizeClass: "medium" },
    "weimaraner": { archetype: "standard", sizeClass: "large" },
    "welsh terrier": { archetype: "standard", sizeClass: "small" },
    "west highland white terrier": { archetype: "standard", sizeClass: "small" },
    "xoloitzcuintli": { archetype: "standard", sizeClass: "medium" },
    "yorkshire terrier": { archetype: "toy", sizeClass: "toy" }
  };

  function buildForArchetype(archetype, sizeClass) {
    if (archetype === "sighthound") return "Slender / Elegant";
    if (archetype === "toy") return "Petite / Low Profile";
    if (archetype === "bully") return (sizeClass === "toy" || sizeClass === "small") ? "Compact / Broad" : "Heavy / Muscular";
    return (sizeClass === "large" || sizeClass === "giant") ? "Athletic / Standard" : "Proportional / Low Profile";
  }

  function deriveFromArchetype(name, entry) {
    var base = SIZE_CLASS_BASE[entry.sizeClass];
    var mul = ARCHETYPE_MULTIPLIER[entry.archetype];
    var neck = [Math.round(base.neck[0] * mul.neck), Math.round(base.neck[1] * mul.neck)];
    var chest = [Math.round(base.chest[0] * mul.chest), Math.round(base.chest[1] * mul.chest)];
    return {
      neck: neck,
      chest: chest,
      neckDefault: Math.round((neck[0] + neck[1]) / 2),
      chestDefault: Math.round((chest[0] + chest[1]) / 2),
      build: buildForArchetype(entry.archetype, entry.sizeClass),
      category: entry.archetype,
      sizeClass: entry.sizeClass
    };
  }

  var MIXED_FALLBACK = {
    neck: null,
    chest: null,
    neckDefault: 30,
    chestDefault: 46,
    build: "Standard Proportions",
    category: "standard",
    isFallback: true
  };

  var BUILD_OPTIONS = [
    "Slender / Elegant", "Proportional / Low Profile", "Petite / Low Profile",
    "Heavy / Muscular", "Compact / Broad", "Athletic / Standard", "Standard Proportions"
  ];

  var BUILD_OPTION_LABELS = {
    "Slender / Elegant": "Slender / Elegant — Long & Delicate Neck (e.g., Whippet, Greyhound, Saluki)",
    "Athletic / Standard": "Athletic / Standard — Athletic / Standard Neck (e.g., Retriever, German Shepherd, Pointer, Vizsla)",
    "Standard Proportions": "Standard Proportions — Standard Neck (e.g., Border Collie, Spaniel, Aussie, Jack Russell)",
    "Proportional / Low Profile": "Proportional / Low Profile — Short Legs & Standard Neck (e.g., Dachshund, Corgi, Basset Hound)",
    "Petite / Low Profile": "Petite / Low Profile — Petite / Miniature Breed (e.g., Toy Poodle, Yorkie, Maltese, Pomeranian)",
    "Heavy / Muscular": "Heavy / Muscular — Thick & Muscular Neck (e.g., Staffy, Pit Bull, Rottweiler, Boxer)",
    "Compact / Broad": "Compact / Broad — Compact & Broad Neck (e.g., French Bulldog, Pug, Boston Terrier, Bullmastiff)"
  };

  // "Compact / Broad" is deliberately omitted — it's the one build label
  // that isn't anatomically unambiguous (CURATED_BREEDS pairs it with both
  // true bully breeds AND non-bully standard breeds), so forcing an
  // override for it would silently reclassify some of those breeds.
  var BUILD_TO_CATEGORY = {
    "Slender / Elegant": "sighthound",
    "Petite / Low Profile": "toy",
    "Heavy / Muscular": "bully",
    "Athletic / Standard": "standard",
    "Proportional / Low Profile": "standard",
    "Standard Proportions": "standard"
  };

  var SIGHTHOUND_KEYWORDS = ["whippet", "greyhound", "borzoi", "saluki", "afghan", "wolfhound"];

  function matchesSighthoundKeyword(normalizedText) {
    return SIGHTHOUND_KEYWORDS.some(function (kw) { return normalizedText.indexOf(kw) !== -1; });
  }

  function escapeHtml(str) {
    return String(str == null ? "" : str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function capitalizeFirst(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  // Resolves any typed/selected breed name to a sizing profile. Priority:
  // curated hand-measured entry > archetype-derived entry > sighthound
  // fuzzy-text safety net (for typed mixes like "Whippet Mix") > generic
  // Mixed Breed / Other fallback. An empty name stays empty rather than
  // being relabeled — that label is reserved for an explicit choice.
  function resolveBreedProfile(name) {
    var trimmed = String(name || "").trim();
    var key = trimmed.toLowerCase();
    if (key && CURATED_BREEDS[key]) {
      return Object.assign({ name: escapeHtml(trimmed) }, CURATED_BREEDS[key]);
    }
    if (key && BREED_ARCHETYPES[key]) {
      return Object.assign({ name: escapeHtml(trimmed) }, deriveFromArchetype(trimmed, BREED_ARCHETYPES[key]));
    }
    if (key && matchesSighthoundKeyword(key)) {
      return Object.assign({ name: escapeHtml(trimmed) }, MIXED_FALLBACK, { category: "sighthound", build: "Slender / Elegant" });
    }
    return Object.assign({ name: trimmed ? escapeHtml(trimmed) : "" }, MIXED_FALLBACK);
  }

  /* ============================================================
     Sizing matrices, boundary ties, guardrails
     ============================================================ */

  var COLLAR_SIZES = ["XXS", "XS", "S", "M", "L", "XL"];
  var NECK_NODES = [17, 22, 27, 32, 37, 42, 47];
  // The neck slider is allowed to travel past the last standard node
  // (47cm / XL) up to this ceiling — the "premium custom collar" zone.
  var NECK_CUSTOM_MAX = 65;

  var HARNESS_SIZES = ["XXXS", "XXS", "XS", "S", "M", "L", "XL"];
  var CHEST_NODES = [26, 30, 34, 38, 46, 54, 64, 76];
  var CHEST_CUSTOM_MAX = 120;

  function sizeFromValue(cm, nodes, sizes) {
    for (var i = 0; i < nodes.length - 1; i++) if (cm < nodes[i + 1]) return sizes[i];
    return sizes[sizes.length - 1];
  }

  function nearestBoundary(cm, nodes) {
    var best = null, bestDist = Infinity;
    nodes.forEach(function (n, i) {
      var d = Math.abs(cm - n);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return { index: best, dist: bestDist };
  }

  function boundaryLetters(nodeIndex, sizes) {
    return {
      lower: sizes[Math.max(0, nodeIndex - 1)],
      upper: sizes[Math.min(sizes.length - 1, nodeIndex)]
    };
  }

  // Tolerance, not exact equality. Storage is now full floating-point
  // precision (see lillebjorn-size-guide.js's inToCm), so a value only
  // ever lands EXACTLY on an integer-cm node when it was typed directly in
  // cm — a shopper typing the chart's own boundary label in inches (e.g.
  // "16.5" for the 42cm/16.5354in node) never can, because that label is
  // itself the node pre-rounded to the nearest 0.1in for display. That
  // rounding has a maximum error of 0.05in (≈0.127cm), so anything within
  // BOUNDARY_TIE_EPSILON_CM of a node is "typed the displayed boundary" by
  // measurement-precision standards. It's comfortably smaller than the
  // narrowest gap between two real nodes (4cm, the tightest chest spacing)
  // and than a genuinely distinct nearby measurement (e.g. 14.4in sits
  // ~0.42cm from the 14.6in/37cm node) — so it only catches true ties,
  // never nudges a legitimately different size into one.
  //
  // 0.127 = half of 0.1in (the finest inch increment either tool's UI
  // resolves to) converted to cm — derived the exact same way
  // lillebjorn-fit-finder.js derives its own BOUNDARY_TIE_EPSILON_CM
  // (there: (INCHES_SLIDER_STEP / 2) * CM_PER_INCH), so both tools flag
  // the same measurement as a tie with zero drift between them.
  var BOUNDARY_TIE_EPSILON_CM = 0.127;

  // A cm value only counts as an interlock-triggering boundary when it
  // lands within BOUNDARY_TIE_EPSILON_CM of a node AND that node genuinely
  // separates two different Size letters (the catalog's absolute
  // floor/ceiling nodes clamp lower===upper, so there's nothing ambiguous
  // to resolve there).
  function boundaryTie(cm, nodes, sizes) {
    var nb = nearestBoundary(cm, nodes);
    if (nb.dist > BOUNDARY_TIE_EPSILON_CM) return null;
    var letters = boundaryLetters(nb.index, sizes);
    if (letters.lower === letters.upper) return null;
    return letters;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function rangeStatus(value, range) {
    if (!range) return { status: "none" };
    if (value < range[0]) {
      var diffLow = range[0] - value;
      return { status: diffLow <= 4 ? "amber" : "red", dir: "below", diff: diffLow };
    }
    if (value > range[1]) {
      var diffHigh = value - range[1];
      return { status: diffHigh <= 4 ? "amber" : "red", dir: "above", diff: diffHigh };
    }
    return { status: "none" };
  }

  function sizeRangeLabel(sizeLetter, nodes, sizes, noun) {
    var idx = sizes.indexOf(sizeLetter);
    if (idx === -1) return "";
    return "Fits " + noun + " " + nodes[idx] + "–" + nodes[idx + 1] + "cm";
  }

  var MEASURE_META = {
    neck: { nodes: NECK_NODES, sizes: COLLAR_SIZES, breedKey: "neck" },
    chest: { nodes: CHEST_NODES, sizes: HARNESS_SIZES, breedKey: "chest" }
  };

  function isCustomNeck(neckSize) { return neckSize > NECK_NODES[NECK_NODES.length - 1]; }
  function isCustomChest(chestSize) { return chestSize > CHEST_NODES[CHEST_NODES.length - 1]; }

  // Strict current-size logic: the returned letter reflects ONLY the cm
  // input plus its LOOSE/SNUG boundary resolution — Growing Puppy never
  // pads this (a loose collar is unsafe); the puppy flag only drives
  // disclaimer copy/starting position elsewhere.
  //
  // Matches lillebjorn-fit-finder.js's computeSize exactly: an exact-tie
  // measurement (dead center on a Size-letter node, e.g. 42cm/16.5in
  // between L and XL) is genuinely ambiguous — a shopper who measured
  // loosely is a real size smaller than one who measured snug — so it is
  // NOT auto-resolved. Callers must supply an explicit boundaryChoice
  // ("loose" or "snug") once the shopper answers the interlock prompt; with
  // no choice yet, this falls through to the plain range bucket, same as
  // any non-boundary value.
  function computeSizeLetter(key, value, boundaryChoice) {
    var meta = MEASURE_META[key];
    var letters = boundaryTie(value, meta.nodes, meta.sizes);
    if (letters && boundaryChoice) {
      return boundaryChoice === "loose" ? letters.lower : letters.upper;
    }
    return sizeFromValue(value, meta.nodes, meta.sizes);
  }

  // Full per-measurement evaluation: size label, breed-relative guardrail
  // (amber/red — suppressed for Mixed Breed/fallback and for Growing Puppy,
  // since a puppy is EXPECTED to measure smaller than the adult breed
  // standard), and boundary-tie state. Mirrors the drawer's
  // buildMeasureVals, minus DOM/state-mutation callbacks.
  function evaluateMeasurement(key, value, opts) {
    opts = opts || {};
    var breed = opts.breedProfile || MIXED_FALLBACK;
    var isPuppy = opts.ageProfile === "puppy";
    var isFallback = !!breed.isFallback;
    var meta = MEASURE_META[key];
    var rs = (isFallback || isPuppy) ? { status: "none" } : rangeStatus(value, breed[meta.breedKey]);
    var letters = boundaryTie(value, meta.nodes, meta.sizes);
    var isBoundary = !!letters;
    var liveSize = sizeFromValue(value, meta.nodes, meta.sizes);
    var isCustom = key === "neck" ? isCustomNeck(value) : isCustomChest(value);
    var sizeLabel = isCustom ? "Custom Size" : (isBoundary ? "Size " + letters.lower + "/" + letters.upper + " Boundary" : "Size " + liveSize);

    var guardrailMessage = "";
    if (rs.status === "amber") {
      guardrailMessage = "A little outside the typical range for " + breed.name + " — not unusual for an individual dog, just worth a second measurement to be sure.";
    } else if (rs.status === "red") {
      guardrailMessage = "Double-check that measurement. " + breed.name + " typically measures neck " + breed.neck[0] + "–" + breed.neck[1] + "cm and chest " + breed.chest[0] + "–" + breed.chest[1] + "cm. Yours is noticeably outside that range — re-measure snugly, or continue if your dog is genuinely outside breed-standard proportions.";
    }

    return {
      sizeLabel: sizeLabel,
      isCustom: isCustom,
      showGuardrail: rs.status !== "none",
      guardrailTone: rs.status === "red" ? "red" : "amber",
      guardrailMessage: guardrailMessage,
      showBoundary: isBoundary,
      boundaryLower: isBoundary ? letters.lower : null,
      boundaryUpper: isBoundary ? letters.upper : null,
      boundaryHeaderText: isBoundary ? "Right on a boundary between Size " + letters.lower + " and Size " + letters.upper + ". How did you measure?" : ""
    };
  }

  // A measurement is "blocked" (CTA disabled) when it sits exactly on a
  // genuine Size-letter tie and hasn't been resolved with LOOSE/SNUG.
  // Never blocked once a measurement is already in the custom-size zone —
  // that CTA becomes a mailto link with no disabled state.
  function isMeasureBlocked(measurements) {
    for (var i = 0; i < measurements.length; i++) {
      var m = measurements[i];
      if (m.isCustom) continue;
      if (m.showBoundary && !m.boundaryChoice) return true;
    }
    return false;
  }

  // Growing Puppy starting position: a flat 35% shrink off the breed's
  // adult baseline. Ceiling is the *_CUSTOM_MAX (not the standard-matrix
  // ceiling) so a breed whose real adult default already exceeds standard
  // sizing (e.g. Great Dane) lands in the custom-size zone immediately.
  var PUPPY_SHRINK_FACTOR = 0.65;

  function applyAgeProfile(breedProfile, ageProfile) {
    var factor = ageProfile === "puppy" ? PUPPY_SHRINK_FACTOR : 1;
    return {
      neckSize: clamp(Math.round(breedProfile.neckDefault * factor), NECK_NODES[0], NECK_CUSTOM_MAX),
      chestSize: clamp(Math.round(breedProfile.chestDefault * factor), CHEST_NODES[0], CHEST_CUSTOM_MAX)
    };
  }

  /* ============================================================
     Custom-size (bespoke) request trigger
     ============================================================ */

  function customBannerText(isCustomCollar, isCustomHarness) {
    var noun = isCustomCollar && isCustomHarness ? "collars and harnesses"
      : isCustomHarness ? "harnesses"
      : "collars";
    return "Looking for a larger size? We handcraft custom " + noun + " tailored precisely to your dog's measurements. Click below to request a bespoke order.";
  }

  // Builds the bespoke-order mailto URL. params: { breedName, ageProfile,
  // breedProfile, neckSize, chestSize, isCustomCollar, isCustomHarness }.
  function buildCustomSizeMailto(params) {
    var breedLabel = params.breedName && params.breedName.trim() ? params.breedName.trim() : "Not specified";
    var ageLabel = params.ageProfile === "puppy" ? "Growing Puppy" : "Adult Dog";
    var sizeClassLabel = params.breedProfile && params.breedProfile.sizeClass
      ? capitalizeFirst(params.breedProfile.sizeClass)
      : "Not specified";
    var dateLabel = new Date().toLocaleDateString();

    var itemLabel = params.isCustomCollar && params.isCustomHarness ? "Collar & Harness Bundle"
      : params.isCustomHarness ? "Harness"
      : "Collar";

    var measurementLines = [];
    if (params.isCustomCollar) measurementLines.push("• Neck Circumference: " + params.neckSize + " cm");
    if (params.isCustomHarness) measurementLines.push("• Chest Circumference: " + params.chestSize + " cm");
    measurementLines.push("• Date of Measurement: " + dateLabel);

    var subjectMeasure = params.isCustomCollar && params.isCustomHarness
      ? params.neckSize + "cm neck / " + params.chestSize + "cm chest"
      : params.isCustomHarness ? params.chestSize + "cm chest" : params.neckSize + "cm neck";

    var subject = "Bespoke " + itemLabel + " Request — " + breedLabel + " (" + subjectMeasure + ")";
    var body = [
      "Hello The Lille Björn Team,",
      "",
      "I would like to request a custom " + itemLabel.toLowerCase() + " size tailored specifically for my dog. Here are our measurements and details from Find My Dog's Size:",
      "",
      "DOG DETAILS",
      "• Breed: " + breedLabel,
      "• Age Status: " + ageLabel,
      "• Estimated Adult Size Class: " + sizeClassLabel,
      "",
      "MEASUREMENTS"
    ].concat(measurementLines).concat([
      "",
      "CUSTOMER NOTES & PREFERENCES",
      "• Preferred Style/Color (if any): ",
      "• Special Requirements (e.g. active puller, sensitive skin): ",
      "",
      "Please let me know the next steps, estimated crafting time, and pricing for this custom order. Thank you!",
      "",
      "Best regards,",
      ""
    ]).join("\n");

    var supportEmail = params.supportEmail || "support@thelillebjorn.com";
    return "mailto:" + supportEmail + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);
  }

  /* ============================================================
     Anatomical strap-width matrix & recommendation rules
     ============================================================ */

  var PRODUCT_FAMILIES = {
    "aeris-classic": { type: "collar", widths: ["2cm", "3cm"], widthSplit: true, defaultWidth: "3cm" },
    "vero-classic": { type: "collar", widths: ["2cm", "3cm"], widthSplit: true },
    "aeris-wide": { type: "collar", widths: ["4.5cm"], defaultWidth: "4.5cm" },
    "vero-wide": { type: "collar", widths: ["4.5cm"], defaultWidth: "4.5cm" },
    "harness": { type: "harness", widths: [] }
  };

  // Family membership (which product LINES are offered) is necessary but
  // not sufficient — see CATEGORY_WIDTH_ALLOWLIST for the hard anatomical
  // gate on which WIDTHS within those families are actually safe to show.
  var RECOMMENDATION_RULES = {
    "sighthound": { main: ["aeris-wide", "vero-wide"], alt: ["aeris-classic", "vero-classic"] },
    "toy": { main: ["vero-classic"], alt: [] },
    "bully": { main: ["aeris-classic", "vero-classic"], alt: [] },
    "standard": { main: ["aeris-classic", "vero-classic"], alt: ["aeris-wide"] }
  };

  // Hard anatomical width gate, independent of and narrower than the
  // family lists above:
  //   - bully: strictly 3cm only (a 2cm collar is never appropriate for a
  //     thick/muscular neck base, regardless of size bracket).
  //   - toy: strictly 2cm only (3cm is unnecessarily heavy for a delicate
  //     frame).
  //   - sighthound: 4.5cm for the main recommendation, 3cm for the
  //     cross-sell alt — never 2cm (undercuts the throat-safety reasoning
  //     Wide exists for).
  //   - standard has no fixed allowlist — both 2cm and 3cm stay available,
  //     subject to the large-neck ceiling below.
  var CATEGORY_WIDTH_ALLOWLIST = {
    "bully": ["3cm"],
    "toy": ["2cm"],
    "sighthound": ["3cm", "4.5cm"]
  };

  // Global large-neck ceiling, layered on top of the allowlist above and
  // applied regardless of category: once a dog's resolved collar size
  // reaches XL (neck >= 42cm), a 2cm collar is never offered again — too
  // flimsy for that circumference no matter the breed's build.
  function isWidthAllowedForCategory(width, category, familyType, recNeckSize) {
    if (familyType !== "collar" || !width) return true; // harnesses have no width option
    var allowlist = CATEGORY_WIDTH_ALLOWLIST[category];
    if (allowlist && allowlist.indexOf(width) === -1) return false;
    if (width === "2cm" && recNeckSize === "XL") return false;
    return true;
  }

  // Widths a given family may show for this category/neck-size, filtered
  // through both gates above, in the family's own declared order.
  function getAllowedWidths(familyKey, category, recNeckSize) {
    var family = PRODUCT_FAMILIES[familyKey];
    if (!family) return [];
    return family.widths.filter(function (w) {
      return isWidthAllowedForCategory(w, category, family.type, recNeckSize);
    });
  }

  // "Standard" carries no single canonical width in the source system
  // (small/medium standard breeds legitimately browse both 2cm and 3cm as
  // separate cards) — for a single-number summary display (not a
  // multi-card browser) we resolve that ambiguity with the same per-size
  // heuristic the design brief used: 2cm for XXS/XS/S, 3cm for M/L,
  // deferring to the allowlist/XL-ceiling for every other category, which
  // are already strict single-width categories. This is a documented
  // judgment call, not a production-confirmed rule.
  function pickPrimaryWidth(familyKey, category, recNeckSize) {
    var allowed = getAllowedWidths(familyKey, category, recNeckSize);
    if (allowed.length === 0) return null;
    if (allowed.length === 1) return allowed[0];
    if (category === "standard") {
      var smallLetters = ["XXS", "XS", "S"];
      return smallLetters.indexOf(recNeckSize) !== -1 ? "2cm" : "3cm";
    }
    return allowed[0];
  }

  // breed.name is blank whenever no breed was ever typed — both the plain
  // raw-measurement flow AND a manual Body & Neck Build pick made without
  // typing a breed (currentBreedProfile overrides category from the build
  // but never touches name). Every WHY_TEXT template below reads breed.name
  // directly, so past this guard every one of them would render as the
  // grammatically broken "Given your 's slender neck..." instead of a
  // generic "your dog's".
  function breedPossessive(breed) {
    return (breed.name ? ("your " + breed.name) : "your dog") + "'s";
  }
  var WHY_TEXT = {
    "sighthound": function (breed) { return "Given " + breedPossessive(breed) + " slender, elegant neck, we prioritized a wider 4.5cm profile to distribute pressure evenly and protect the throat during quick turns."; },
    "bully": function (breed) { return "With " + breedPossessive(breed) + " powerful shoulders and thicker neck base, we matched our true-to-size Classic collar in a robust 3cm width for a precise, supportive fit."; },
    "toy": function (breed) { return "Given " + breedPossessive(breed) + " delicate frame, we matched a featherweight, narrow profile for a light, comfortable fit."; },
    "standard": function (breed) { return "We tailored this recommendation to " + breedPossessive(breed) + " build and measurements for a precise, comfortable fit."; }
  };

  // Step 4 puppy safety disclaimers, keyed by breed.category. The width
  // recommendation itself never changes for Growing Puppy — only this copy.
  var PUPPY_COLLAR_DISCLAIMER = {
    "sighthound": "We recommended our Wide 4.5cm collar to properly protect and support your sighthound's delicate, long neck. Since your puppy is still growing, check the fit regularly to ensure safety.",
    "toy": "We recommended our ultra-light 2cm collar for your puppy's delicate frame. Since they are still growing, please check the collar tightness regularly.",
    "bully": "We recommended our comfortable 3cm Classic collar for your puppy's current proportions. Since your puppy is still growing, please check the fit regularly.",
    "standard": "We recommended our comfortable 2cm/3cm Classic collar for your puppy's current proportions. Since your puppy is still growing, please check the fit regularly."
  };
  var PUPPY_HARNESS_DISCLAIMER = "Since your puppy is still growing, please adjust the harness straps regularly to ensure a safe, secure fit as their proportions develop.";

  var OPTIONS = [
    { id: "collar", label: "Just a Collar", sub: "Tailored neck essentials for daily elegance.", types: ["collar"] },
    { id: "harness", label: "Just a Harness", sub: "Structured support for the everyday adventurer.", types: ["harness"] },
    { id: "bundle", label: "Collar & Harness Bundle", sub: "A complete wardrobe, cut from the same hide.", types: ["collar", "harness"] }
  ];

  /* ============================================================
     Leash rules (real catalog: 1.5cm / 2cm widths, Classic / Hands Free
     styles) & bundle/walk-kit pricing math
     ============================================================ */

  var LEASH_WIDTH_BY_COLLAR_SIZE = {
    XXS: "1.5cm", XS: "1.5cm", S: "1.5cm",
    M: "2cm", L: "2cm", XL: "2cm"
  };
  var LEASH_WIDTH_BY_HARNESS_SIZE = {
    XXXS: "1.5cm", XXS: "1.5cm", XS: "1.5cm", S: "1.5cm",
    M: "2cm", L: "2cm", XL: "2cm"
  };

  var LEASH_STYLE_DEFAULT = "Classic";
  var LEASH_STYLE_OPTIONS = ["Classic", "Hands Free"];
  var LEASH_STYLE_DISPLAY_NAME = { "Classic": "Classic", "Hands Free": "Omni Hands Free" };
  var LEASH_STYLE_DESCRIPTIONS = {
    "Classic": "Traditional handheld lead — clip it on and hold, just like your dog's current leash.",
    "Hands Free": "Adjustable cross-body strap — worn hands-free for runs, hikes, or everyday walks."
  };
  var LEASH_STYLE_SUBTITLE = { "Classic": "Handheld", "Hands Free": "Cross-body" };

  var LEATHERS = [
    { key: "black", name: "Black", hex: "#111111" },
    { key: "red", name: "Red", hex: "#B31F24" },
    { key: "green", name: "Green", hex: "#1E4620" },
    { key: "blue", name: "Blue", hex: "#1D3354" },
    { key: "brown", name: "Brown", hex: "#9E5B38" }
  ];

  // Fallback only — the real rate is always read from the merchant's own
  // section setting (data-bundle-discount-percent on the live drawer;
  // the Size Guide section should read its own equivalent setting).
  var UPSELL_DISCOUNT_RATE_DEFAULT = 0.10;
  var TIER_ADD_LABELS = { single: "ADD TO CART", walkkit: "ADD WALK KIT", bundle: "ADD COMPLETE BUNDLE", base: "ADD COLLAR & HARNESS" };

  // Shared discount math: set price = round(sum(itemCents) * (1 - rate)).
  // itemsCents: array of integer cent amounts. Returns null if any item is
  // missing a price (mirrors the drawer's own "degrade to single tier"
  // behavior rather than showing a broken partial discount).
  function computeBundlePricing(itemsCents, discountRate) {
    for (var i = 0; i < itemsCents.length; i++) {
      if (itemsCents[i] == null) return null;
    }
    var setCents = itemsCents.reduce(function (a, b) { return a + b; }, 0);
    var discountedCents = Math.round(setCents * (1 - discountRate));
    return {
      setCents: setCents,
      discountedCents: discountedCents,
      savingsCents: setCents - discountedCents,
      badge: "SAVE " + Math.round(discountRate * 100) + "%"
    };
  }

  /* ============================================================
     Public API
     ============================================================ */

  global.LillebjornFitLogic = {
    // Breed data & resolution
    BREED_NAMES: BREED_NAMES,
    FALLBACK_BREED_NAME: FALLBACK_BREED_NAME,
    CURATED_BREEDS: CURATED_BREEDS,
    BREED_ARCHETYPES: BREED_ARCHETYPES,
    MIXED_FALLBACK: MIXED_FALLBACK,
    BUILD_OPTIONS: BUILD_OPTIONS,
    BUILD_OPTION_LABELS: BUILD_OPTION_LABELS,
    BUILD_TO_CATEGORY: BUILD_TO_CATEGORY,
    resolveBreedProfile: resolveBreedProfile,
    escapeHtml: escapeHtml,
    capitalizeFirst: capitalizeFirst,

    // Sizing matrices & guardrails
    COLLAR_SIZES: COLLAR_SIZES,
    NECK_NODES: NECK_NODES,
    NECK_CUSTOM_MAX: NECK_CUSTOM_MAX,
    HARNESS_SIZES: HARNESS_SIZES,
    CHEST_NODES: CHEST_NODES,
    CHEST_CUSTOM_MAX: CHEST_CUSTOM_MAX,
    MEASURE_META: MEASURE_META,
    clamp: clamp,
    sizeFromValue: sizeFromValue,
    boundaryTie: boundaryTie,
    rangeStatus: rangeStatus,
    sizeRangeLabel: sizeRangeLabel,
    computeSizeLetter: computeSizeLetter,
    evaluateMeasurement: evaluateMeasurement,
    isMeasureBlocked: isMeasureBlocked,
    isCustomNeck: isCustomNeck,
    isCustomChest: isCustomChest,

    // Age/build
    PUPPY_SHRINK_FACTOR: PUPPY_SHRINK_FACTOR,
    applyAgeProfile: applyAgeProfile,

    // Custom-size trigger
    customBannerText: customBannerText,
    buildCustomSizeMailto: buildCustomSizeMailto,

    // Width matrix & recommendation rules
    PRODUCT_FAMILIES: PRODUCT_FAMILIES,
    RECOMMENDATION_RULES: RECOMMENDATION_RULES,
    CATEGORY_WIDTH_ALLOWLIST: CATEGORY_WIDTH_ALLOWLIST,
    isWidthAllowedForCategory: isWidthAllowedForCategory,
    getAllowedWidths: getAllowedWidths,
    pickPrimaryWidth: pickPrimaryWidth,
    WHY_TEXT: WHY_TEXT,
    PUPPY_COLLAR_DISCLAIMER: PUPPY_COLLAR_DISCLAIMER,
    PUPPY_HARNESS_DISCLAIMER: PUPPY_HARNESS_DISCLAIMER,
    OPTIONS: OPTIONS,

    // Leash & bundle pricing
    LEASH_WIDTH_BY_COLLAR_SIZE: LEASH_WIDTH_BY_COLLAR_SIZE,
    LEASH_WIDTH_BY_HARNESS_SIZE: LEASH_WIDTH_BY_HARNESS_SIZE,
    LEASH_STYLE_DEFAULT: LEASH_STYLE_DEFAULT,
    LEASH_STYLE_OPTIONS: LEASH_STYLE_OPTIONS,
    LEASH_STYLE_DISPLAY_NAME: LEASH_STYLE_DISPLAY_NAME,
    LEASH_STYLE_DESCRIPTIONS: LEASH_STYLE_DESCRIPTIONS,
    LEASH_STYLE_SUBTITLE: LEASH_STYLE_SUBTITLE,
    LEATHERS: LEATHERS,
    UPSELL_DISCOUNT_RATE_DEFAULT: UPSELL_DISCOUNT_RATE_DEFAULT,
    TIER_ADD_LABELS: TIER_ADD_LABELS,
    computeBundlePricing: computeBundlePricing
  };
})(window);
